import { Checkbox, Chip, Group, NumberInput, Select, Stack, Text } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { MemoryDatePrecision } from 'src/Services/API/Memories.api';
import { decadeLabel, MemorySeason } from 'src/Utils/memoryDate';

import styles from './MemoryDateInput.module.scss';
import { MemoryDatePrecisionChoice, MemoryDateValue } from './memoryDateValue';

interface Props {
  value: MemoryDateValue;
  onChange: (value: MemoryDateValue) => void;
}

const CURRENT_YEAR = new Date().getFullYear();
const FIRST_DECADE = 1900;

/**
 * "Quand ?" for people who don't always remember the exact day: a precision first (day,
 * month, season, year, decade or no date), then only the fields that precision needs.
 */
const MemoryDateInput = ({ value, onChange }: Props) => {
  const { t } = useTranslation();
  const set = (patch: Partial<MemoryDateValue>) => onChange({ ...value, ...patch });

  const precisions: { value: MemoryDatePrecisionChoice; label: string }[] = [
    { value: MemoryDatePrecision.Day, label: t('memories.date.day', 'Le jour exact') },
    { value: MemoryDatePrecision.Month, label: t('memories.date.month', 'Le mois') },
    { value: MemoryDatePrecision.Season, label: t('memories.date.season', 'La saison') },
    { value: MemoryDatePrecision.Year, label: t('memories.date.year', "L'année") },
    { value: MemoryDatePrecision.Decade, label: t('memories.date.decade', 'La décennie') },
    { value: 'NONE', label: t('memories.date.none', 'Je ne sais pas') },
  ];
  const months = Array.from({ length: 12 }, (_, index) => ({
    value: String(index),
    label: dayjs().month(index).format('MMMM'),
  }));
  const seasons: { value: MemorySeason; label: string }[] = [
    { value: 'SPRING', label: t('memories.seasonNames.SPRING', 'Printemps') },
    { value: 'SUMMER', label: t('memories.seasonNames.SUMMER', 'Été') },
    { value: 'AUTUMN', label: t('memories.seasonNames.AUTUMN', 'Automne') },
    { value: 'WINTER', label: t('memories.seasonNames.WINTER', 'Hiver') },
  ];
  const decades = Array.from(
    { length: Math.floor(CURRENT_YEAR / 10) - FIRST_DECADE / 10 + 1 },
    (_, index) => FIRST_DECADE + index * 10
  )
    .reverse()
    .map(decade => ({
      value: String(decade),
      label: decadeLabel(t, decade),
    }));

  const yearInput = (
    <NumberInput
      size="md"
      label={t('memories.date.yearLabel', 'Année')}
      min={1800}
      max={CURRENT_YEAR}
      allowDecimal={false}
      hideControls
      value={value.year ?? ''}
      onChange={year => set({ year: typeof year === 'number' ? year : null })}
      className={styles.year}
    />
  );

  return (
    <Stack gap="sm">
      <Text fw={500}>{t('memories.date.label', 'Quand ?')}</Text>
      <Chip.Group
        value={value.precision}
        onChange={precision => set({ precision: precision as MemoryDatePrecisionChoice })}
      >
        <Group gap="xs">
          {precisions.map(precision => (
            <Chip key={precision.value} value={precision.value} size="md">
              {precision.label}
            </Chip>
          ))}
        </Group>
      </Chip.Group>

      {value.precision === MemoryDatePrecision.Day && (
        <DatePickerInput
          size="md"
          valueFormat="DD/MM/YYYY"
          maxDate={new Date()}
          value={value.day}
          onChange={day => set({ day })}
          placeholder={t('memories.date.pickDay', 'Choisir le jour')}
        />
      )}
      {value.precision === MemoryDatePrecision.Month && (
        <Group grow align="flex-end">
          <Select
            size="md"
            label={t('memories.date.monthLabel', 'Mois')}
            data={months}
            value={value.month !== null ? String(value.month) : null}
            onChange={month => set({ month: month !== null ? Number(month) : null })}
          />
          {yearInput}
        </Group>
      )}
      {value.precision === MemoryDatePrecision.Season && (
        <Group grow align="flex-end">
          <Select
            size="md"
            label={t('memories.date.seasonLabel', 'Saison')}
            data={seasons}
            value={value.season}
            onChange={season => set({ season: season as MemorySeason | null })}
          />
          {yearInput}
        </Group>
      )}
      {value.precision === MemoryDatePrecision.Year && yearInput}
      {value.precision === MemoryDatePrecision.Decade && (
        <Select
          size="md"
          data={decades}
          value={value.decade !== null ? String(value.decade) : null}
          onChange={decade => set({ decade: decade !== null ? Number(decade) : null })}
          placeholder={t('memories.date.pickDecade', 'Choisir la décennie')}
        />
      )}

      {value.precision !== 'NONE' && (
        <Checkbox
          size="md"
          label={t('memories.date.approximate', 'À peu près')}
          checked={value.approximate}
          onChange={event => set({ approximate: event.currentTarget.checked })}
        />
      )}
    </Stack>
  );
};

export default MemoryDateInput;
